import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Save, Edit, Plus, Trash2, Building2, Users, CreditCard, Shield, User as UserIcon, RefreshCw } from 'lucide-react';
import { mockAutoApprovalRules } from '../data/mockData';
import { AutoApprovalRule, User } from '../types';
import { AutoApprovalRuleModal, AddUserModal } from '../components/modals';
import { useCompanyProfile, useUserProfile } from '../hooks';

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>('company');
  const [autoApprovalRules, setAutoApprovalRules] = useState<AutoApprovalRule[]>(mockAutoApprovalRules);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Use API hooks
  const { 
    company, 
    loading: companyLoading, 
    error: companyError, 
    updateCompanyProfile, 
    refreshData: refreshCompany 
  } = useCompanyProfile();

  const { 
    profile, 
    loading: profileLoading, 
    error: profileError, 
    updateUserProfile, 
    refreshData: refreshProfile 
  } = useUserProfile();
  
  // Modal state
  const [showAutoApprovalRule, setShowAutoApprovalRule] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoApprovalRule | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Security tab state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showSecurityLogsModal, setShowSecurityLogsModal] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  // Handle URL tab parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['company', 'auto-approval', 'users', 'cashback', 'profile', 'account', 'security'].includes(tabParam)) {
      // Map profile/account/security to existing tabs
      const tabMapping: { [key: string]: string } = {
        'profile': 'company',
        'account': 'users',
        'security': 'auto-approval'
      };
      setActiveTab(tabMapping[tabParam] || tabParam);
    }
  }, [searchParams]);

  const handleSaveCompanyProfile = async () => {
    if (!company) return;
    
    setIsSaving(true);
    try {
      await updateCompanyProfile({
        name: company.name,
        industry: company.industry,
        address: company.address,
        phone: company.phone,
        website: company.website
      });
      setIsEditing(false);
      alert('Company profile updated successfully!');
    } catch (error) {
      console.error('Error updating company profile:', error);
      alert('Failed to update company profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL parameter
    setSearchParams({ tab: tabId });
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      alert('New password must be at least 8 characters long!');
      return;
    }

    setIsChangingPassword(true);
    try {
      // Here you would call your API to change password
      // For now, we'll simulate the API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      alert('Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Security tab handlers
  const handle2FAToggle = () => {
    if (is2FAEnabled) {
      if (window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
        setIs2FAEnabled(false);
        alert('2FA has been disabled.');
      }
    } else {
      setShow2FAModal(true);
    }
  };

  const handleViewSessions = () => {
    setShowSessionsModal(true);
  };

  const handleViewSecurityLogs = () => {
    setShowSecurityLogsModal(true);
  };

  const handleToggleRule = (id: string) => {
    setAutoApprovalRules(prev => 
      prev.map(rule => 
        rule.id === id ? { ...rule, isActive: !rule.isActive } : rule
      )
    );
  };

  const handleDeleteRule = (id: string) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      setAutoApprovalRules(prev => prev.filter(rule => rule.id !== id));
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setShowAutoApprovalRule(true);
  };

  const handleEditRule = (rule: AutoApprovalRule) => {
    setEditingRule(rule);
    setShowAutoApprovalRule(true);
  };

  const handleSaveRule = (ruleData: Omit<AutoApprovalRule, 'id'>) => {
    if (editingRule) {
      // Update existing rule
      setAutoApprovalRules(prev => 
        prev.map(rule => 
          rule.id === editingRule.id 
            ? { ...ruleData, id: rule.id }
            : rule
        )
      );
      alert('Auto-approval rule updated successfully!');
    } else {
      // Add new rule
      const newRule: AutoApprovalRule = {
        ...ruleData,
        id: `rule_${Date.now()}`
      };
      setAutoApprovalRules(prev => [...prev, newRule]);
      alert('Auto-approval rule created successfully!');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowAddUser(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowAddUser(true);
  };

  const handleSaveUser = (userData: Omit<User, 'id' | 'lastLogin' | 'createdAt'>) => {
    if (editingUser) {
      // Update existing user
      alert('User updated successfully! (Check console for details)');
      console.log('Updating user:', editingUser.id, userData);
    } else {
      // Add new user
      alert('User created successfully! (Check console for details)');
      console.log('Adding new user:', userData);
    }
    setShowAddUser(false);
    setEditingUser(null);
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      alert('User deleted successfully! (Check console for details)');
      console.log('Deleting user:', userId);
    }
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'company':
      case 'profile':
        if (companyLoading) {
          return (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading company profile...</p>
              </div>
            </div>
          );
        }

        if (companyError) {
          return (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Company Profile</h3>
              <p className="text-gray-600 mb-4">{companyError}</p>
              <button 
                onClick={refreshCompany}
                className="btn-primary"
              >
                Try Again
              </button>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Company Profile</h3>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="btn-secondary"
              >
                <Edit className="h-4 w-4 mr-2" />
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={company?.name || ''}
                  onChange={(e) => {
                    if (company) {
                      company.name = e.target.value;
                    }
                  }}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <input
                  type="text"
                  value={company?.industry || ''}
                  onChange={(e) => {
                    if (company) {
                      company.industry = e.target.value;
                    }
                  }}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={company?.phone || ''}
                  onChange={(e) => {
                    if (company) {
                      company.phone = e.target.value;
                    }
                  }}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={company?.website || ''}
                  onChange={(e) => {
                    if (company) {
                      company.website = e.target.value;
                    }
                  }}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  value={company?.address || ''}
                  onChange={(e) => {
                    if (company) {
                      company.address = e.target.value;
                    }
                  }}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end">
                <button
                  onClick={handleSaveCompanyProfile}
                  disabled={isSaving}
                  className="btn-primary flex items-center"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

            case 'auto-approval':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Auto-Approval Rules</h3>
              <button
                onClick={handleAddRule}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </button>
            </div>

            <div className="space-y-4">
              {autoApprovalRules.map((rule) => (
                <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{rule.name}</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          rule.type === 'credit_limit' ? 'bg-blue-100 text-blue-800' :
                          rule.type === 'wallet_load' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {rule.type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{rule.condition}</p>
                      
                      {rule.maxAmount && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Max Amount:</span> ₦{rule.maxAmount.toLocaleString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditRule(rule)}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Edit Rule"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={rule.isActive}
                          onChange={() => handleToggleRule(rule.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Active</span>
                      </label>
                      
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Rule"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

            case 'users':
      case 'account':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {activeTab === 'account' ? 'Account Settings' : 'User Management'}
              </h3>
              <button
                onClick={handleAddUser}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
            </div>

            <div className="card">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">User</th>
                      <th className="table-header">Role</th>
                      <th className="table-header">Department</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Last Login</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Admin User</div>
                          <div className="text-sm text-gray-500">admin@dangote.com</div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Super Admin
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm text-gray-900">IT</span>
                      </td>
                      <td className="table-cell">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm text-gray-500">2 hours ago</span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleEditUser({
                              id: '1',
                              name: 'Admin User',
                              email: 'admin@dangote.com',
                              role: 'super_admin',
                              department: 'IT',
                              status: 'active',
                              lastLogin: '2 hours ago',
                              createdAt: '2023-01-01'
                            })}
                            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            title="Edit User"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteUser('1')}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Security Settings</h3>
            </div>

            {/* Password Change Section */}
            <div className="card">
              <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter your current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter new password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex justify-end">
                  <button 
                    onClick={handlePasswordChange}
                    disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>

            {/* Two-Factor Authentication Section */}
            <div className="card">
              <h4 className="text-md font-medium text-gray-900 mb-4">Two-Factor Authentication</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">2FA Status</p>
                    <p className="text-sm text-gray-500">
                      {is2FAEnabled ? 'Two-factor authentication is currently enabled' : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                  <button 
                    onClick={handle2FAToggle}
                    className={`btn-secondary ${is2FAEnabled ? 'bg-red-100 text-red-700 hover:bg-red-200' : ''}`}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {is2FAEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                  </button>
                </div>
              </div>
            </div>

            {/* Session Management Section */}
            <div className="card">
              <h4 className="text-md font-medium text-gray-900 mb-4">Session Management</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Active Sessions</p>
                    <p className="text-sm text-gray-500">Manage your active login sessions</p>
                  </div>
                  <button 
                    onClick={handleViewSessions}
                    className="btn-secondary"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    View Sessions
                  </button>
                </div>
              </div>
            </div>

            {/* Security Logs Section */}
            <div className="card">
              <h4 className="text-md font-medium text-gray-900 mb-4">Security Activity</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Recent Security Events</p>
                    <p className="text-sm text-gray-500">Monitor your account security activity</p>
                  </div>
                  <button 
                    onClick={handleViewSecurityLogs}
                    className="btn-secondary"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    View Logs
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'cashback':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Cashback Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <h4 className="text-md font-medium text-gray-900 mb-4">General Cashback</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Cashback Rate (%)
                    </label>
                    <input
                      type="number"
                      defaultValue="2.5"
                      min="0"
                      max="10"
                      step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Transaction Amount
                    </label>
                    <input
                      type="number"
                      defaultValue="1000"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="card">
                <h4 className="text-md font-medium text-gray-900 mb-4">Category-Based Cashback</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dining & Entertainment (%)
                    </label>
                    <input
                      type="number"
                      defaultValue="5.0"
                      min="0"
                      max="15"
                      step="0.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Travel & Transportation (%)
                    </label>
                    <input
                      type="number"
                      defaultValue="3.0"
                      min="0"
                      max="10"
                      step="0.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button className="btn-primary">
                <Save className="h-4 w-4 mr-2" />
                Save Cashback Settings
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Configure your corporate portal settings and preferences</p>
      </div>

      {/* Settings Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'company', name: 'Company Profile', icon: Building2 },
              { id: 'auto-approval', name: 'Auto-Approval Rules', icon: Shield },
              { id: 'users', name: 'User Management', icon: Users },
              { id: 'security', name: 'Security', icon: Shield },
              { id: 'cashback', name: 'Cashback Settings', icon: CreditCard },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="py-6">
          {getTabContent()}
        </div>
      </div>

      {/* Auto-Approval Rule Modal */}
      <AutoApprovalRuleModal
        isOpen={showAutoApprovalRule}
        onClose={() => setShowAutoApprovalRule(false)}
        rule={editingRule}
        onSave={handleSaveRule}
      />

      {/* Add/Edit User Modal */}
      <AddUserModal
        isOpen={showAddUser}
        onClose={() => {
          setShowAddUser(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
        user={editingUser}
      />

      {/* 2FA Setup Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enable Two-Factor Authentication</h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose your preferred 2FA method to secure your account.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => {
                  setShow2FAModal(false);
                  setIs2FAEnabled(true);
                  alert('SMS 2FA has been enabled!');
                }}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900">SMS Authentication</div>
                <div className="text-sm text-gray-500">Receive codes via text message</div>
              </button>
              <button 
                onClick={() => {
                  setShow2FAModal(false);
                  setIs2FAEnabled(true);
                  alert('Email 2FA has been enabled!');
                }}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900">Email Authentication</div>
                <div className="text-sm text-gray-500">Receive codes via email</div>
              </button>
              <button 
                onClick={() => {
                  setShow2FAModal(false);
                  setIs2FAEnabled(true);
                  alert('Authenticator App 2FA has been enabled!');
                }}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="font-medium text-gray-900">Authenticator App</div>
                <div className="text-sm text-gray-500">Use apps like Google Authenticator</div>
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setShow2FAModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Modal */}
      {showSessionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Sessions</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Current Session</div>
                  <div className="text-sm text-gray-500">Chrome on macOS • Active now</div>
                  <div className="text-xs text-gray-400">IP: 192.168.1.100 • Last activity: Just now</div>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Current</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Mobile Session</div>
                  <div className="text-sm text-gray-500">Safari on iPhone • 2 hours ago</div>
                  <div className="text-xs text-gray-400">IP: 192.168.1.101 • Last activity: 2 hours ago</div>
                </div>
                <button className="text-red-600 hover:text-red-800 text-sm">Terminate</button>
              </div>
            </div>
            <div className="flex justify-end">
              <button 
                onClick={() => setShowSessionsModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Logs Modal */}
      {showSecurityLogsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Activity Log</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Login Success</div>
                  <div className="text-sm text-gray-500">Successful login from Chrome on macOS</div>
                  <div className="text-xs text-gray-400">IP: 192.168.1.100 • 2 minutes ago</div>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Success</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Password Changed</div>
                  <div className="text-sm text-gray-500">Password was successfully updated</div>
                  <div className="text-xs text-gray-400">IP: 192.168.1.100 • 1 hour ago</div>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Info</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Failed Login Attempt</div>
                  <div className="text-sm text-gray-500">Failed login from unknown device</div>
                  <div className="text-xs text-gray-400">IP: 203.0.113.1 • 3 hours ago</div>
                </div>
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Warning</span>
              </div>
            </div>
            <div className="flex justify-end">
              <button 
                onClick={() => setShowSecurityLogsModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
